import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
} from "material-react-table";
import {
  Eye16Filled,
  Edit16Filled,
  FilterDismiss20Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import {
  Button,
  Input,
  makeStyles,
  shorthands,
  tokens,
  Tooltip,
  useId,
} from "@fluentui/react-components";
import ASCButton from "../../components/Button";
import {
  checkUserPermission,
  formatAmount,
  getAmountType,
  getCommissionCategory,
  getUserDefinedType,
  getYes_NoConversion,
  stripHtmlTags,
} from "../../utils";
import ReactQuillComponent from "../../components/ReactQuillComponent";
import { UserPermissionsList } from "../../config";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  findRowIndexByKey,
  getContextIdKey,
  getRowKey,
  useRestoreRowSelection,
} from "../../components/tableRowSelection";
import { withCurrencyColumns } from "../../components/Common/currencyColumn";

const useStyles = makeStyles({
  wrapper: {
    columnGap: "15px",
    display: "flex",
    cursor: "pointer",
  },
  contentHeader: {
    marginTop: "0",
  },
  searchBar: {
    alignItems: "flex-start",
    columnGap: tokens.spacingHorizontalS,
    display: "flex",
  },
  searchField: {
    width: "325px",
    // Let it give way to the Apply/Clear buttons in a narrow list pane.
    maxWidth: "100%",
    minWidth: 0,
  },
  searchHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  emptyRowsFallback: {
    // The cell spans every column, so on a table wider than its container the
    // text would centre on the full scroll width (off screen). Pin it to the
    // visible area instead; its width is set to the container's at render.
    position: "sticky",
    left: 0,
    boxSizing: "border-box",
    textAlign: "center",
    whiteSpace: "normal",
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    color: "#f00",
  },
  scopeArea: {
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    ...shorthands.gap("2px"),
    maxWidth: "400px",
  },
});

type ComponentProps = {
  style?: CSSProperties;
  [key: string]: any;
};

const tableColumnList = {
  SYSTEM_MANAGER_USERLIST: [
    {
      accessorKey: "full_name",
      header: "Full Name",
    },
    {
      accessorKey: "short_name",
      header: "Short Name",
    },
    {
      accessorKey: "user_email",
      header: "Email",
    },
    {
      accessorKey: "active",
      header: "Active",
      Cell: ({ row }: any) => <>{getYes_NoConversion(row.original.active)}</>,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getYes_NoConversion(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
    },
    // {
    //   accessorKey: "role",
    //   header: "Role",
    // },
    // {
    //   accessorKey: "permissions",
    //   header: "Permissions",
    //   Cell: ({ row }: any) => <>{row.original.permissions.join(",")}</>,
    // },
  ],
  MASTER_SETTINGS_INCOME_CATEGORY: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "auto_add",
      header: "Auto Add",
      Cell: ({ row }: any) => <>{row.original.auto_add === 0 ? "No" : "Yes"}</>,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getYes_NoConversion(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
    },
  ],
  MASTER_SETTINGS_EXPENSE_CATEGORY: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "discretionary",
      header: "Type",
    },
    {
      accessorKey: "auto_add",
      header: "Auto Add",
      Cell: ({ row }: any) => <>{row.original.auto_add === 0 ? "No" : "Yes"}</>,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getYes_NoConversion(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
    },
  ],
  MASTER_SETTINGS_ATTITUDE_TO_RISK_CATEGORY: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "category",
      header: "Category",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "auto_add",
      header: "Auto Add",
      Cell: ({ row }: any) => <>{row.original.auto_add === 0 ? "No" : "Yes"}</>,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getYes_NoConversion(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
    },
  ],
  MASTER_SETTINGS_ATTITUDE_TO_RISK_RATING: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "rating",
      header: "Rating",
    },
    {
      accessorKey: "rating_score",
      header: "Score",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
  ],
  MASTER_SETTINGS_OBJECTIVES: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },

    {
      accessorKey: "detail",
      header: "Detail",
    },
    {
      accessorKey: "auto_add",
      header: "Auto Add",
      Cell: ({ row }: any) => <>{row.original.auto_add === 0 ? "No" : "Yes"}</>,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getYes_NoConversion(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
    },
  ],
  MASTER_SETTINGS_USER_DEFINED: [
    {
      accessorKey: "name",
      header: "Name",
    },

    {
      accessorKey: "type",
      header: "Type",
      Cell: ({ row }: any) => <>{getUserDefinedType(row.original.type)}</>,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getUserDefinedType(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
    },
    {
      accessorKey: "length",
      header: "Length",
    },
  ],
  MASTER_SETTINGS_PROVIDERS: [
    {
      accessorKey: "group_name",
      header: "Group Name",
    },

    {
      accessorKey: "provider_name",
      header: "Provider Name",
    },
    {
      accessorKey: "contact_name",
      header: "Contact",
    },
    {
      accessorKey: "address1",
      header: "Address 1",
    },
    {
      accessorKey: "address2",
      header: "Address 2",
    },
    {
      accessorKey: "address3",
      header: "Address 3",
    },
    {
      accessorKey: "town",
      header: "Town",
    },
    {
      accessorKey: "county",
      header: "County",
    },
    {
      accessorKey: "country",
      header: "Country",
    },
    {
      accessorKey: "postcode",
      header: "Postcode",
    },

    {
      accessorKey: "salutation",
      header: "Salutation",
    },
    {
      accessorKey: "telephone",
      header: "Telephone",
    },
    {
      accessorKey: "fax",
      header: "Fax",
    },
    {
      accessorKey: "mobile",
      header: "Mobile",
    },
    {
      accessorKey: "email",
      header: "Email",
    },

    {
      accessorKey: "status",
      header: "Status",
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  MASTER_SETTINGS_CASE_ACTIONS: [
    {
      accessorKey: "case_type",
      header: "Action Group",
    },
    {
      accessorKey: "due_interval",
      header: "Due Interval(Days)",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "assigned",
      header: "Assigned",
    },
  ],
  MASTER_SETTINGS_CLIENT_ACTIONS: [
    {
      accessorKey: "case_type",
      header: "Action Group",
    },
    {
      accessorKey: "due_interval",
      header: "Due Interval(Days)",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "assigned",
      header: "Assigned",
    },
  ],
  COMMISSION_TYPES: [
    {
      accessorKey: "category",
      header: "Category",
      Cell: ({ row }: any) => (
        <>{getCommissionCategory(row.original.category)}</>
      ),
    },
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "commission_type",
      header: "Commission Type",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
  ],
  COMMISSION_RULES: [
    {
      accessorKey: "party_type",
      header: "Party Type",
    },
    {
      accessorKey: "case_type",
      header: "Case Type",
      Cell: ({ row }: any) => {
        const selectedRowItem = row.original;
        return (
          <>
            {selectedRowItem.case_type === "Unit-linked"
              ? "Asset (Investment)"
              : selectedRowItem.case_type}
          </>
        );
      },
    },
    {
      accessorKey: "party_name",
      header: "Party Name",
    },
    {
      accessorKey: "amount",
      header: "Amount",
      Cell: ({ row }: any) => {
        const selectedRowItem = row.original;
        return (
          <>
            {formatAmount(selectedRowItem.amount, selectedRowItem.amount_flag)}
          </>
        );
      },
    },
    {
      accessorKey: "amount_flag",
      header: "Amount Type",
      Cell: ({ row }: any) => <>{getAmountType(row.original.amount_flag)}</>,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getAmountType(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
};

const TableDataList = (props: ComponentProps) => {
  const styles = useStyles();
  const searchHintId = useId("provider-search-hint");
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const loggedUser = useSelector((state: RootState) => state.authUser);
  const tableRef = React.useRef<HTMLDivElement>(null);
  // Set while the selection is being restored from the active record, so the
  // effect below can tell it apart from a user clicking a row.
  const restoringSelectionRef = React.useRef(false);
  const idKey = getContextIdKey(props.context);

  const customColumns = useMemo<MRT_ColumnDef<{ [key: string]: any }>[]>(() => {
    if (props.context !== "") {
      return withCurrencyColumns(
        (tableColumnList as { [key: string]: any })[props.context] ?? [],
        props.context,
      );
    }

    return [];
  }, [props.context]);

  useEffect(() => {
    // A selection restored from the active record must not re-fire the row
    // action: it navigates, so it would rewrite `/edit` back to `/view` and
    // push a history entry every time the page is refreshed.
    if (restoringSelectionRef.current) {
      restoringSelectionRef.current = false;
      return;
    }
    if (
      props.handleRowAction !== undefined &&
      props.data.length > 0 &&
      Object.keys(rowSelection)[0] !== undefined
    ) {
      const index = findRowIndexByKey(
        props.data,
        Object.keys(rowSelection)[0],
        idKey,
      );
      const selectedRow = index < 0 ? undefined : props.data[index];
      if (selectedRow) {
        props.handleRowAction("view", selectedRow);
      }
    }
  }, [rowSelection]);
  // Re-apply the selection whenever the active record or the list changes.
  // This grid used to compute the record number here but never set
  // `rowSelection`, so after a refresh the right record was open in the form
  // while the list showed no highlighted row at all.
  useRestoreRowSelection({
    data: props.data,
    idKey,
    selectedId: idKey ? props.selectedRowItem?.[idKey] : undefined,
    rowSelection,
    setRowSelection,
    setCurrentRecord: props.setCurrentRecord,
    onBeforeRestore: () => {
      restoringSelectionRef.current = true;
    },
    containerRef: tableRef,
  });

  return (
    <div ref={tableRef}>
      {customColumns && customColumns.length > 0 && (
        <MaterialReactTable
          columns={customColumns as any}
          data={props.data}
          enableRowSelection
          enablePagination={props.context === "MASTER_SETTINGS_PROVIDERS"}
          enableColumnActions={false}
          onRowSelectionChange={setRowSelection}
          enableBottomToolbar={props.context === "MASTER_SETTINGS_PROVIDERS"}
          rowNumberMode="static"
          enableMultiRowSelection={false}
          muiTableBodyRowProps={({ row }) => ({
            onClick: row.getToggleSelectedHandler(),
            sx: { cursor: "pointer" },
          })}
          getRowId={(row, index) => getRowKey(row, index, idKey)}
          enableRowActions={false}
          displayColumnDefOptions={{
            "mrt-row-actions": {
              header: "Actions", //change header text
              // size: 300, //make actions column wider
              muiTableHeadCellProps: {
                align: "center",
              },
              muiTableBodyCellProps: {
                align: "center",
              },
            },
          }}
          renderRowActions={({ row, table }) => (
            <div className={styles.wrapper}>
              {checkUserPermission(
                loggedUser,
                UserPermissionsList.ALLOW_VIEW_USER,
              ) && (
                <Tooltip
                  content="View"
                  relationship="label"
                  positioning="below"
                >
                  <ASCButton
                    shape="rounded"
                    appearance="subtle"
                    icon={
                      <Eye16Filled
                        primaryFill="var(--button-color)"
                        onClick={() => {
                          props.handleRowAction("view", row);
                        }}
                      />
                    }
                  />
                </Tooltip>
              )}
              <Tooltip content="Edit" relationship="label" positioning="below">
                <ASCButton
                  shape="rounded"
                  appearance="subtle"
                  icon={
                    <Edit16Filled
                      primaryFill="var(--button-color)"
                      onClick={() => {
                        props.handleRowAction("edit", row);
                      }}
                    />
                  }
                />
              </Tooltip>
            </div>
          )}
          initialState={{
            columnPinning: { right: ["mrt-row-actions"] },
          }}
          positionActionsColumn="last"
          positionToolbarAlertBanner="none"
          state={{
            rowSelection,
            ...(props.context === "MASTER_SETTINGS_PROVIDERS"
              ? {
                  pagination: props.pagination,
                  // Load in place so the toolbar (and the keyword in it) stays
                  // put instead of the whole list blinking out and back.
                  isLoading: !!props.loading,
                }
              : {}),
          }}
          {...(props.context === "MASTER_SETTINGS_PROVIDERS" && {
            manualPagination: true,
            // The toolbar carries its own search field, so turn off MRT's
            // built-in one and its client-side filtering.
            enableGlobalFilter: false,
            manualFiltering: true,
            onPaginationChange: props.setPagination,
            rowCount: props.rowCount,
            renderEmptyRowsFallback: ({ table }) => (
              <div
                className={styles.emptyRowsFallback}
                // Same sizing MRT's default fallback uses: the visible width of
                // the table's container, never the scrollable table width.
                style={{
                  width:
                    table.refs.tableContainerRef.current?.clientWidth ?? "100%",
                }}
              >
                {props.error
                  ? "Unable to load providers. Please try again."
                  : props.search?.listFilter
                    ? "No matching records found for the entered search criteria"
                    : "No records to display"}
              </div>
            ),
            renderTopToolbarCustomActions: () =>
              props.search ? (
                <div className={styles.searchBar}>
                  <div style={{display: "flex", flexDirection: "column"}}>
                  <Input
                    className={styles.searchField}
                    value={props.search.input}
                    placeholder="Search by Provider Name or Group Name"
                    aria-label="Search providers"
                    aria-describedby={searchHintId}
                    contentBefore={<Search20Regular />}
                    onChange={(_, data) =>
                      props.search.onInputChange(data.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && props.search.canApply) {
                        props.search.onApply();
                      }
                    }}
                  />
                                    {/* Explains why Apply is still disabled for a short term.
                      Last in the row so it doesn't shift the buttons. */}
                  <span
                    id={searchHintId}
                    className={styles.searchHint}
                    aria-live="polite"
                  >
                    {props.search.input.trim() !== "" && !props.search.canApply
                      ? `Enter at least ${props.search.minLength} characters`
                      : ""}
                  </span>
                  </div>
                  <ASCButton
                    shape="rounded"
                    appearance="primary"
                    className="asc-button-primary"
                    size="medium"
                    disabled={!props.search.canApply}
                    onClick={props.search.onApply}
                    label="Apply"
                  />
                  {props.search.applied !== "" && (
                    <Tooltip content="Clear filter" relationship="label">
                      <Button
                        icon={<FilterDismiss20Regular />}
                        onClick={props.search.onClear}
                      />
                    </Tooltip>
                  )}

                </div>
              ) : null,
          })}
        />
      )}
    </div>
  );
};

export default TableDataList;
